import { BoxOfferDropInIcon } from "@/assets/images/svgIcons/boxOffer/dropInIcon"
import { Button } from "@/components/ui/baseUi/button"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { cn } from "@/modules/lib/utils"
import { BoxLeadTable } from "@modules/boxLead/components/boxLeadTable"
import { useBoxLeadSidebar } from "@modules/boxLead/hooks/useBoxLeadSidebar"
import { BoxStaffTable } from "@modules/boxStaff/components/boxStaffTable"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { useBoxStaffSidebar } from "@modules/boxStaff/hooks/useBoxStaffSidebar"
import { word } from "@modules/core/utils/i18n"
import { BoxArchivedMembersAndStaff } from "@modules/members/boxTerminatedMembers/components/boxArchivedMembersAndStaff"
import { MemberTable } from "@modules/members/components/memberTable"
import { MemberFilter } from "@modules/members/gateway/memberDto"
import { ScreenHeader } from "@modules/ui/components/header"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { isInArray } from "@modules/utils/isInArray"
import { useSetMultiParams } from "@modules/utils/navigation/useQueryParam"
import { BriefcaseBusiness, DoorClosed, DoorOpen, Handshake, Plus, Search, X } from "lucide-react"
import { startTransition, Suspense, useEffect, useState } from "react"
import { useSearchParams } from "react-router"

export function MembersPage({ className, ...props }: React.ComponentProps<"div"> & { className?: string }) {
  const [nameFilter, setNameFilter] = useState("")
  const [countTab, setCountTab] = useState(0)
  const [searchParams] = useSearchParams()
  const memberTab = searchParams.get("tab")
  const setListParams = useSetMultiParams()

  const { navigateToBoxStaffCards } = useBoxStaffSidebar()
  const { navigateToBoxLeadForm } = useBoxLeadSidebar()
  const canSeeTerminated = useBoxStaffPermission(BoxStaffPermissions.SEE_TERMINATED)
  const canAddStaff = useBoxStaffPermission(BoxStaffPermissions.ADD_STAFF)
  const canAddLead = useBoxStaffPermission(BoxStaffPermissions.MANAGE_LEAD)

  const selectedTab = isInArray(Object.values(MemberFilter), memberTab) ? memberTab : MemberFilter.ACTIVE
  const [activeIndex, setActiveIndex] = useState<MemberFilter>(selectedTab.toString() as MemberFilter)

  useEffect(() => {
    const paramsArray = Array.from(searchParams.entries()).map(([key, value]) => ({ key, value }))
    setListParams([...paramsArray, { key: "nameFilter", value: nameFilter ?? "" }])
  }, [nameFilter])

  const handleTabChange = (index: MemberFilter) => {
    startTransition(() => {
      setActiveIndex(index)
      // const data = membersOptions.find((option) => option.data === index)?.data
      setListParams([
        { key: "tab", value: index },
        { key: "page", value: "0" },
        { key: "sortBy", value: undefined },
        { key: "order", value: undefined },
      ])
    })
  }

  return (
    <div className={cn("flex min-w-245 grow flex-col gap-0 p-6", className)} {...props}>
      {/* HEADER PAGE */}
      <div className="flex justify-between">
        <ScreenHeader title={word("member.page.title")} subTitle={word("member.page.subtitle")} />
        {activeIndex === MemberFilter.PROSPECT && canAddLead && (
          <Button variant="primary" size="lg" onClick={() => navigateToBoxLeadForm()}>
            <Plus size={16} />
            {word("member.button.addLead")}
          </Button>
        )}
        {activeIndex === MemberFilter.STAFF && canAddStaff && (
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              navigateToBoxStaffCards()
            }}
          >
            <Plus size={16} />
            {word("member.button.addStaff")}
          </Button>
        )}
      </div>

      <div className="flex grow flex-col gap-6 pt-2">
        {/* HEADER TABLE (Menu & Search) */}
        <div className="flex flex-row items-baseline px-2">
          <Tabs defaultValue={activeIndex} className="flex w-full flex-col">
            <TabsList variant="profile" className="w-full group-data-horizontal/tabs:h-fit">
              <div className="flex flex-col items-center">
                <TabsTrigger
                  variant="profile"
                  onClick={() => {
                    handleTabChange(MemberFilter.ACTIVE)
                  }}
                  value="active"
                  className="h-14 items-baseline"
                >
                  <Handshake size={16} color="currentColor" className="mr-2" />
                  <span className="font-bold">{word("member.filter.active")}</span>
                </TabsTrigger>
                <span className="mt-1">{activeIndex === MemberFilter.ACTIVE ? `(${countTab})` : ""}</span>
              </div>
              <div className="flex flex-col items-center">
                <TabsTrigger
                  variant="profile"
                  onClick={() => {
                    handleTabChange(MemberFilter.PROSPECT)
                  }}
                  value="prospect"
                  className="h-14 items-baseline"
                >
                  <DoorOpen size={16} color="currentColor" className="mr-2" />
                  <span className="font-bold">{word("member.filter.prospect")}</span>
                </TabsTrigger>
                <span className="mt-1">{activeIndex === MemberFilter.PROSPECT ? `(${countTab})` : ""}</span>
              </div>

              <div className="flex flex-col items-center">
                <TabsTrigger
                  variant="profile"
                  onClick={() => {
                    handleTabChange(MemberFilter.DROP_IN)
                  }}
                  value="drop_in"
                  className="h-14 items-baseline"
                >
                  <BoxOfferDropInIcon size={16} color="currentColor" className="mr-2" />
                  <span className="font-bold">{word("member.filter.dropIn")}</span>
                </TabsTrigger>
                <span className="mt-1">{activeIndex === MemberFilter.DROP_IN ? `(${countTab})` : ""}</span>
              </div>
              <div className="flex flex-col items-center">
                <TabsTrigger
                  variant="profile"
                  onClick={() => {
                    handleTabChange(MemberFilter.STAFF)
                  }}
                  value="staff"
                  className="h-14 items-baseline"
                >
                  <BriefcaseBusiness size={16} color="currentColor" className="mr-2" />
                  <span className="font-bold">{word("member.filter.staff")}</span>
                </TabsTrigger>
                <span className="mt-1">{activeIndex === MemberFilter.STAFF ? `(${countTab})` : ""}</span>
              </div>

              {canSeeTerminated && (
                <div className="flex flex-col items-center">
                  <TabsTrigger
                    variant="profile"
                    onClick={() => {
                      handleTabChange(MemberFilter.TERMINATED)
                    }}
                    value="terminated"
                    className="h-14 items-baseline"
                  >
                    <DoorClosed size={16} color="currentColor" className="mr-2" />
                    <span className="font-bold">{word("member.filter.terminated")}</span>
                  </TabsTrigger>
                  <span className="mt-1">{activeIndex === MemberFilter.TERMINATED ? `(${countTab})` : ""}</span>
                </div>
              )}
            </TabsList>
          </Tabs>

          <InputGroup className="w-fit">
            <InputGroupInput
              placeholder={word("global.search.placeholder")}
              onChange={(e) => {
                setNameFilter(e.currentTarget.value)
              }}
              value={nameFilter}
            />
            <InputGroupAddon align="inline-start">
              <Search />
            </InputGroupAddon>
            {nameFilter && (
              <InputGroupAddon
                align="inline-end"
                onClick={() => {
                  setNameFilter("")
                }}
              >
                <X color="currentColor" className="cursor-pointer" />
              </InputGroupAddon>
            )}
          </InputGroup>
        </div>

        {/* TABLE */}
        {/* Note : The suspense needs distinct fallback for each table to work properly */}
        {selectedTab === MemberFilter.ACTIVE && (
          <Suspense fallback={<LoadingSpinner />}>
            <MemberTable memberTab={selectedTab} setCountTab={setCountTab} />
          </Suspense>
        )}

        {selectedTab === MemberFilter.STAFF && (
          <Suspense fallback={<LoadingSpinner />}>
            <BoxStaffTable setCountTab={setCountTab} />
          </Suspense>
        )}
        {selectedTab === MemberFilter.PROSPECT && (
          <Suspense fallback={<LoadingSpinner />}>
            <BoxLeadTable setCountTab={setCountTab} />
          </Suspense>
        )}
        {selectedTab === MemberFilter.TERMINATED && canSeeTerminated && (
          <Suspense fallback={<LoadingSpinner />}>
            <BoxArchivedMembersAndStaff setCountTab={setCountTab} />
          </Suspense>
        )}

        {selectedTab === MemberFilter.DROP_IN && ( // separate table for drop_in to avoid parameter confusion with active
          <Suspense fallback={<LoadingSpinner />}>
            <MemberTable memberTab={selectedTab} setCountTab={setCountTab} />
          </Suspense>
        )}
      </div>
    </div>
  )
}
